import dayjs from 'dayjs';
import { isEqual } from 'lodash-es';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { DEFAULT_CALL_INTEL_TIME_PERIOD } from '../constants';

export type CallIntelSubViewType = 'appointment-type' | 'category' | 'sentiment' | 'service-quality';

export type CallIntelSubViewId =
  | string
  | CallIntelTypes.AppointmentTypeEnum
  | CallIntelTypes.CategoryEnum
  | CallIntelTypes.SentimentEnum;

export type CallIntelSubView = {
  id?: CallIntelSubViewId;
  type?: CallIntelSubViewType;
};

export type MultiLocationsView = 'summary' | 'betweenLocations' | 'withinLocations';

interface CallIntelStore {
  activeMultiView?: MultiLocationsView;
  activeTab: CallIntelTypes.CallIntelTab;
  defaultFilters: CallIntelTypes.Filters;
  filterHintText: string;
  filters: CallIntelTypes.Filters;
  filtersToRestore?: CallIntelTypes.Filters;
  hasCustomFilters: boolean;
  initialQueryParamsFilters: CallIntelTypes.Filters;
  locationIds: string[];
  selectedTimePeriodKey: CallIntelTypes.SelectedTimePeriodKey;
  subView: CallIntelSubView;
  timePeriodKeyToRestore: CallIntelTypes.SelectedTimePeriodKey;
  trayFilters: CallIntelTypes.Filters;

  setActiveMultiView: (activeMultiView: MultiLocationsView) => void;
  setActiveTab: (activeTab: CallIntelTypes.CallIntelTab) => void;
  setDefaultFilters: (defaultFilters: Partial<CallIntelTypes.Filters>) => void;
  setFilterHintText: (filterHintText: string) => void;
  setFilters: (filters: Partial<CallIntelTypes.Filters>) => void;
  setFiltersToRestore: (filtersToRestore?: CallIntelTypes.Filters) => void;
  setInitialQueryParamsFilters: (filters: CallIntelTypes.Filters) => void;
  setLocationIds: (locationId: string[]) => void;
  setSelectedTimePeriodKey: (selectedPeriod: CallIntelTypes.SelectedTimePeriodKey) => void;
  setSubView: (subView: CallIntelSubView) => void;
  setTrayFilters: (trayFilters: CallIntelTypes.Filters) => void;
  resetFilters: () => void;
  resetStore: () => void;
}

const useCallIntelStore = createStoreWithSubscribe<CallIntelStore>(
  (set, get) => ({
    activeTab: 'overview',
    availableAppointmentTypes: [],
    availableCategories: [],
    chartLabelColors: {},
    chartOutlineColors: {},
    chipVariants: {},
    dataColors: {},
    dataLabels: {},
    dataHighlightColors: {},
    defaultFilters: {},
    filterHintText: '',
    filters: {},
    filtersToRestore: undefined,
    hasCustomFilters: false,
    headerChipVariants: {},
    initialQueryParamsFilters: {},
    locationIds: [],
    selectedTimePeriodKey: DEFAULT_CALL_INTEL_TIME_PERIOD,
    subView: {},
    timePeriodKeyToRestore: null,
    trayFilters: {},

    setActiveTab: (activeTab) => {
      set({ activeTab });
    },
    setActiveMultiView: (activeMultiView) => {
      set({ activeMultiView });
    },
    setDefaultFilters: (defaultFilters) => {
      set({ defaultFilters });
    },
    setFilterHintText: (filterHintText) => {
      set({ filterHintText });
    },
    setFilters: (filters) => {
      const { defaultFilters = {}, filters: currentFilters } = get();
      const newFilters: CallIntelTypes.Filters = { ...currentFilters, ...filters };

      const {
        startDate: defaultStartDate,
        endDate: defaultEndDate,
        locations: defaultLocations,
        ...otherDefaults
      } = defaultFilters;

      const areDatesEqual =
        dayjs(newFilters.startDate).isSame(dayjs(defaultStartDate), 'date') &&
        dayjs(newFilters.endDate).isSame(dayjs(defaultEndDate), 'date');

      const areLocationsEqual = isEqual(newFilters.locations?.sort(), defaultLocations?.sort());

      set({
        filters: newFilters,
        hasCustomFilters:
          !areDatesEqual ||
          !areLocationsEqual ||
          Object.keys(otherDefaults).some(
            (key) =>
              !isEqual(
                newFilters[key as keyof CallIntelTypes.Filters],
                defaultFilters[key as keyof CallIntelTypes.Filters]
              )
          ),
      });
    },
    setFiltersToRestore: (filtersToRestore) => {
      const { selectedTimePeriodKey } = get();

      set({ filtersToRestore, timePeriodKeyToRestore: selectedTimePeriodKey });
    },
    setInitialQueryParamsFilters: (filters) => {
      set({ initialQueryParamsFilters: filters });
    },
    setLocationIds: (locationIds) => {
      set({ locationIds });
    },
    setSelectedTimePeriodKey: (selectedPeriod) => {
      set({ selectedTimePeriodKey: selectedPeriod });
    },
    setSubView: (subView) => {
      set({ subView });
    },
    setTrayFilters: (trayFilters) => {
      set({ trayFilters });
    },
    resetFilters: () => {
      const { defaultFilters, setFilters } = get();
      setFilters(defaultFilters);
    },
    resetStore: () => {
      set({
        activeMultiView: undefined,
        defaultFilters: {},
        filterHintText: '',
        filters: {},
        filtersToRestore: undefined,
        initialQueryParamsFilters: {},
        hasCustomFilters: false,
        selectedTimePeriodKey: DEFAULT_CALL_INTEL_TIME_PERIOD,
        subView: {},
        timePeriodKeyToRestore: null,
        trayFilters: {},
      });
    },
  }),
  {
    name: 'CallIntelStore',
    trace: true,
  }
);

export const useCallIntelShallowStore = createShallowStore<CallIntelStore>(useCallIntelStore);
